import { useLayoutEffect, useRef, useState, type PointerEvent, type RefObject } from "react";

const DRAG_THRESHOLD_PX = 5;

type DragState = {
  pointerId: number;
  startX: number;
  startOffset: number;
  moved: boolean;
};

function wrapOffset({ offset, halfWidth }: { offset: number; halfWidth: number }): number {
  if (halfWidth <= 0) return 0;
  let next = offset % halfWidth;
  if (next > 0) next -= halfWidth;
  return next;
}

export function applyScrollerOffset({
  scroller,
  offsetRef,
  offset,
}: {
  scroller: HTMLDivElement;
  offsetRef: { current: number };
  offset: number;
}) {
  const wrapped = wrapOffset({ offset, halfWidth: scroller.scrollWidth / 2 });
  offsetRef.current = wrapped;
  scroller.style.transform = `translate3d(${wrapped}px, 0, 0)`;
}

function armDrag({
  event,
  offset,
  dragRef,
}: {
  event: PointerEvent<HTMLDivElement>;
  offset: number;
  dragRef: { current: DragState | null };
}) {
  dragRef.current = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startOffset: offset,
    moved: false,
  };
}

function activateDrag({
  event,
  drag,
  dragActiveRef,
  setIsDragging,
  onDragStart,
}: {
  event: PointerEvent<HTMLDivElement>;
  drag: DragState;
  dragActiveRef: { current: boolean };
  setIsDragging: (value: boolean) => void;
  onDragStart?: () => void;
}) {
  drag.moved = true;
  dragActiveRef.current = true;
  event.currentTarget.setPointerCapture(event.pointerId);
  setIsDragging(true);
  onDragStart?.();
}

function moveDrag({
  event,
  dragRef,
  scrollerRef,
  offsetRef,
  dragActiveRef,
  setIsDragging,
  onDragStart,
}: {
  event: PointerEvent<HTMLDivElement>;
  dragRef: { current: DragState | null };
  scrollerRef: RefObject<HTMLDivElement | null>;
  offsetRef: { current: number };
  dragActiveRef: { current: boolean };
  setIsDragging: (value: boolean) => void;
  onDragStart?: () => void;
}) {
  const drag = dragRef.current;
  if (!drag || drag.pointerId !== event.pointerId) return;
  const deltaX = event.clientX - drag.startX;
  if (!drag.moved) {
    if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;
    activateDrag({ event, drag, dragActiveRef, setIsDragging, onDragStart });
  }
  const scroller = scrollerRef.current;
  if (scroller) {
    applyScrollerOffset({ scroller, offsetRef, offset: drag.startOffset + deltaX });
  }
}

function finishDrag({
  event,
  dragRef,
  dragActiveRef,
  suppressClickRef,
  setIsDragging,
}: {
  event: PointerEvent<HTMLDivElement>;
  dragRef: { current: DragState | null };
  dragActiveRef: { current: boolean };
  suppressClickRef: { current: boolean };
  setIsDragging: (value: boolean) => void;
}) {
  const drag = dragRef.current;
  if (!drag || drag.pointerId !== event.pointerId) return;
  if (drag.moved) suppressClickRef.current = true;
  dragRef.current = null;
  dragActiveRef.current = false;
  setIsDragging(false);
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
}

type MarqueeDragInput = {
  overflows: boolean;
  scrollerRef: RefObject<HTMLDivElement | null>;
  offsetRef: { current: number };
  dragActiveRef: { current: boolean };
  onDragStart?: () => void;
};

export function useMarqueeDrag({
  overflows,
  scrollerRef,
  offsetRef,
  dragActiveRef,
  onDragStart,
}: MarqueeDragInput) {
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const onDragStartRef = useRef(onDragStart);
  useLayoutEffect(() => {
    onDragStartRef.current = onDragStart;
  }, [onDragStart]);
  const [isDragging, setIsDragging] = useState(false);

  return {
    isDragging,
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (!overflows || event.button !== 0) return;
      armDrag({ event, offset: offsetRef.current, dragRef });
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      moveDrag({
        event,
        dragRef,
        scrollerRef,
        offsetRef,
        dragActiveRef,
        setIsDragging,
        onDragStart: () => onDragStartRef.current?.(),
      });
    },
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
      finishDrag({ event, dragRef, dragActiveRef, suppressClickRef, setIsDragging });
    },
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => {
      finishDrag({ event, dragRef, dragActiveRef, suppressClickRef, setIsDragging });
    },
    onClickCapture: (event: { preventDefault(): void; stopPropagation(): void }) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
